import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney, setUserContext, startDurationVital, stopDurationVital } from '../utils/datadog';
import { getRumUser } from '../utils/users';

type LoanSubmitRequest = components['schemas']['LoanSubmitRequest'];
type LoanSubmitResponse = components['schemas']['LoanSubmitResponse'];

// Dog-themed car makes and models
const CAR_MAKES: Record<string, string[]> = {
  'Bark Motor Works': ['Bark-8', 'Paw-5', 'Woof-3', 'X-K9'],
  Furd: ['Expawrer', 'K-950', 'Muttstang'],
  Furarri: ['LaFurrari', 'Pawtofino', 'Spaniel', 'Testarover'],
  Houndai: ['Barkccent', 'Pawlisade', 'Santa Fetch'],
  Labradorgini: ['Aventadog', 'Huracorgi'],
  Pawdi: ['Arf-4', 'Arf-6', 'Bark-8', 'K9-5'],
  Pawsche: ['Barkster', 'Canine', 'Tailcan'],
  Subawoof: ['Barkback', 'Furester'],
  Woofswagen: ['Beagle', 'Growlf GTI', 'Pawssat'],
};

const CAR_YEARS = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - i).toString());

const LoanPage: React.FC = () => {
  const username = getRumUser();

  // Extract name from username
  const usernameWithoutNumbers = username.replace(/\d+$/, '');
  const [firstName = 'John', lastName = 'Doe'] = usernameWithoutNumbers.split('.');
  const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const capitalizedLastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);
  const fullName = `${capitalizedFirstName} ${capitalizedLastName}`;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: fullName,
    vehicleYear: new Date().getFullYear().toString(),
    vehicleMake: 'Woofswagen',
    vehicleModel: 'Beagle',
    amount: '35,000',
    termMonths: '60',
    ssn: '123-45-6789',
    income: '100,000',
    creditScore: '750',
    employmentStatus: 'employed',
    agreeTerms: true,
  });

  // Get available models based on selected make
  const availableModels = formData.vehicleMake ? CAR_MAKES[formData.vehicleMake] || [] : [];

  // Build full vehicle string
  const getVehicleString = () => {
    if (formData.vehicleYear && formData.vehicleMake && formData.vehicleModel) {
      return `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}`;
    }
    return '';
  };

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<LoanSubmitResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Bitsburg Bank - Auto Loans';

    // Set user context for RUM
    setUserContext(username);

    // Track page visit
    trackUserJourney('loan_page_visited', {
      timestamp: new Date().toISOString(),
    });

    // Start duration vitals for measuring loan application timing
    startDurationVital('loan-application-total', { context: { username } });
    startDurationVital('loan-step-1', { context: { username } });

    // Cleanup on unmount - stop vitals if user abandons the page
    return () => {
      stopDurationVital('loan-step-1', { context: { username, abandoned: true } });
      stopDurationVital('loan-step-2', { context: { username, abandoned: true } });
      stopDurationVital('loan-step-3', { context: { username, abandoned: true } });
      stopDurationVital('loan-application-total', { context: { username, abandoned: true } });
    };
  }, [username]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'vehicleMake') {
      // Reset model when make changes
      setFormData((prev) => ({ ...prev, vehicleMake: value, vehicleModel: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const formatSSN = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 3 && numbers.length <= 5) {
      return numbers.slice(0, 3) + '-' + numbers.slice(3);
    } else if (numbers.length > 5) {
      return numbers.slice(0, 3) + '-' + numbers.slice(3, 5) + '-' + numbers.slice(5, 9);
    }
    return numbers;
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSSN(e.target.value);
    setFormData((prev) => ({ ...prev, ssn: formatted }));
  };

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers) {
      return Number(numbers).toLocaleString();
    }
    return '';
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrency(e.target.value);
    setFormData((prev) => ({ ...prev, amount: formatted }));
  };

  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrency(e.target.value);
    setFormData((prev) => ({ ...prev, income: formatted }));
  };

  const goToStep = (step: number) => {
    // Track step completion for Product Analytics funnel
    if (step > currentStep) {
      // Moving forward - stop current vital and start next
      if (currentStep === 1) {
        stopDurationVital('loan-step-1', { context: { username } });
        startDurationVital('loan-step-2', { context: { username } });
        trackUserJourney('loan_step_1_completed', {
          vehicle: getVehicleString(),
          amount: formData.amount.replace(/,/g, ''),
          timestamp: new Date().toISOString(),
        });
      } else if (currentStep === 2) {
        stopDurationVital('loan-step-2', { context: { username } });
        startDurationVital('loan-step-3', { context: { username } });
        trackUserJourney('loan_step_2_completed', {
          has_ssn: !!formData.ssn,
          has_income: !!formData.income,
          timestamp: new Date().toISOString(),
        });
      }
    } else if (step < currentStep) {
      // Moving backward - stop current vital and restart previous
      if (currentStep === 2) {
        stopDurationVital('loan-step-2', { context: { username, navigatedBack: true } });
        startDurationVital('loan-step-1', { context: { username } });
      } else if (currentStep === 3) {
        stopDurationVital('loan-step-3', { context: { username, navigatedBack: true } });
        startDurationVital('loan-step-2', { context: { username } });
      }
    }
    setCurrentStep(step);
    setError('');
  };

  const validateStep1 = () => {
    if (
      !formData.name ||
      !formData.vehicleYear ||
      !formData.vehicleMake ||
      !formData.vehicleModel ||
      !formData.amount
    ) {
      setError('Please fill in all required fields.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.ssn || !formData.income || !formData.creditScore) {
      setError('Please fill in all required fields.');
      return false;
    }
    if (!/^\d{3}-\d{2}-\d{4}$/.test(formData.ssn)) {
      setError('Please enter a valid SSN in XXX-XX-XXXX format.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      goToStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      goToStep(3);
    }
  };

  const handleAbandon = () => {
    // Only track abandonment if form is in progress (no result yet)
    if (!result) {
      trackUserJourney('loan_application_abandoned', {
        step: currentStep,
        stepName: currentStep === 1 ? 'vehicle_info' : currentStep === 2 ? 'financial_info' : 'review',
        vehicleSelected: !!formData.vehicleMake && !!formData.vehicleModel,
        amountEntered: !!formData.amount,
        creditScoreSelected: !!formData.creditScore && formData.creditScore !== '',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreeTerms) {
      setError('Please accept the terms and conditions.');
      return;
    }

    setIsLoading(true);
    setError('');

    const vehicleString = getVehicleString();
    const request: LoanSubmitRequest = {
      name: formData.name,
      vehicle: vehicleString,
      amount: Number(formData.amount.replace(/,/g, '')),
      termMonths: Number(formData.termMonths),
      ssn: formData.ssn,
      income: Number(formData.income.replace(/,/g, '')),
      creditScore: Number(formData.creditScore),
      employmentStatus: formData.employmentStatus,
    };

    try {
      const response = await fetch('/api/loan/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      // Get response from backend
      const data = (await response.json()) as LoanSubmitResponse;

      // Format monthly payment for display
      const formattedMonthlyPayment = data.monthlyPayment!.toFixed(2);
      data.monthlyPayment = Number(formattedMonthlyPayment);

      setResult(data);

      // Stop duration vitals for loan application timing
      stopDurationVital('loan-step-3', { context: { username } });
      stopDurationVital('loan-application-total', { context: { username } });

      // Track submission for Product Analytics
      trackUserJourney('loan_step_3_submitted', {
        status: data.status,
        amount: request.amount,
        vehicle: vehicleString,
        timestamp: new Date().toISOString(),
      });

      // Track specific outcome for funnel analysis
      if (data.status === 'Approved') {
        trackUserJourney('loan_pre_approved', {
          applicationId: data.applicationId,
          amount: request.amount,
          interestRate: data.interestRate,
          monthlyPayment: data.monthlyPayment,
          timestamp: new Date().toISOString(),
        });
      } else if (data.status === 'Pending Review') {
        trackUserJourney('loan_pending_review', {
          applicationId: data.applicationId,
          amount: request.amount,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      window.DD_LOGS?.logger?.error('Loan submission error', { error: errorMessage });
      window.DD_RUM?.addError(err);

      // Stop duration vitals on error
      stopDurationVital('loan-step-3', { context: { username, error: errorMessage } });
      stopDurationVital('loan-application-total', { context: { username, error: errorMessage } });

      // Track failed submission for Product Analytics
      trackUserJourney('loan_submission_failed', {
        error: errorMessage,
        amount: request.amount,
        vehicle: vehicleString,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Progress bar
  const renderProgress = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between mb-3">
        <span
          className="badge fs-6 px-3 py-2"
          style={{ backgroundColor: currentStep >= 1 ? 'var(--datadog-purple)' : '#6c757d' }}
        >
          1. Vehicle Info
        </span>
        <span
          className="badge fs-6 px-3 py-2"
          style={{ backgroundColor: currentStep >= 2 ? 'var(--datadog-purple)' : '#6c757d' }}
        >
          2. Financial Info
        </span>
        <span
          className="badge fs-6 px-3 py-2"
          style={{ backgroundColor: currentStep >= 3 ? 'var(--datadog-purple)' : '#6c757d' }}
        >
          3. Review & Submit
        </span>
      </div>
      <div className="progress" style={{ height: '8px' }}>
        <div
          className="progress-bar"
          style={{ width: `${((currentStep - 1) / 2) * 100}%`, backgroundColor: 'var(--datadog-purple)' }}
        ></div>
      </div>
    </div>
  );

  // Step 1: Vehicle Info
  const renderStep1 = () => (
    <div>
      <h5 className="mb-4">
        <i className="bi bi-car-front me-2"></i>
        Vehicle Information
      </h5>

      <div className="mb-4">
        <label htmlFor="name" className="bank-form-label">
          Full Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control bank-form-control"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="row">
        <div className="col-md-4 mb-4">
          <label htmlFor="vehicleYear" className="bank-form-label">
            Year <span className="text-danger">*</span>
          </label>
          <select
            className="form-select bank-form-control"
            id="vehicleYear"
            name="vehicleYear"
            value={formData.vehicleYear}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Year</option>
            {CAR_YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-4">
          <label htmlFor="vehicleMake" className="bank-form-label">
            Make <span className="text-danger">*</span>
          </label>
          <select
            className="form-select bank-form-control"
            id="vehicleMake"
            name="vehicleMake"
            value={formData.vehicleMake}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Make</option>
            {Object.keys(CAR_MAKES).map((make) => (
              <option key={make} value={make}>
                {make}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-4">
          <label htmlFor="vehicleModel" className="bank-form-label">
            Model <span className="text-danger">*</span>
          </label>
          <select
            className="form-select bank-form-control"
            id="vehicleModel"
            name="vehicleModel"
            value={formData.vehicleModel}
            onChange={handleInputChange}
            disabled={!formData.vehicleMake}
            required
          >
            <option value="">Select Model</option>
            {availableModels.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="amount" className="bank-form-label">
            Loan Amount <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              className="form-control bank-form-control"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleAmountChange}
              placeholder="35,000"
              required
            />
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <label htmlFor="termMonths" className="bank-form-label">
            Loan Term <span className="text-danger">*</span>
          </label>
          <select
            className="form-select bank-form-control"
            id="termMonths"
            name="termMonths"
            value={formData.termMonths}
            onChange={handleInputChange}
          >
            <option value="36">36 months (3 years)</option>
            <option value="48">48 months (4 years)</option>
            <option value="60">60 months (5 years)</option>
            <option value="72">72 months (6 years)</option>
            <option value="84">84 months (7 years)</option>
          </select>
        </div>
      </div>

      <div className="text-end">
        <button type="button" id="loan-step1-continue-btn" className="bank-btn-primary" onClick={handleNext}>
          Continue <i className="bi bi-arrow-right ms-2"></i>
        </button>
      </div>
    </div>
  );

  // Step 2: Financial Info (where dropoff happens)
  const renderStep2 = () => (
    <div>
      <h5 className="mb-4">
        <i className="bi bi-wallet2 me-2"></i>
        Financial Information
      </h5>

      <div className="alert alert-info mb-4">
        <i className="bi bi-shield-lock me-2"></i>
        Your information is encrypted and secure. We use this to verify your identity and assess your application.
      </div>

      <div className="mb-4">
        <label htmlFor="ssn" className="bank-form-label">
          Social Security Number <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control bank-form-control"
          id="ssn"
          name="ssn"
          value={formData.ssn}
          onChange={handleSSNChange}
          placeholder="XXX-XX-XXXX"
          maxLength={11}
          required
        />
        <small className="text-muted">Required for credit check</small>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="income" className="bank-form-label">
            Annual Income <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              className="form-control bank-form-control"
              id="income"
              name="income"
              value={formData.income}
              onChange={handleIncomeChange}
              placeholder="75,000"
              required
            />
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <label htmlFor="creditScore" className="bank-form-label">
            Estimated Credit Score <span className="text-danger">*</span>
          </label>
          <select
            className="form-select bank-form-control"
            id="creditScore"
            name="creditScore"
            value={formData.creditScore}
            onChange={handleInputChange}
            required
          >
            <option value="">Select range</option>
            <option value="750">Excellent (750+)</option>
            <option value="700">Good (700-749)</option>
            <option value="650">Fair (650-699)</option>
            <option value="600">Poor (600-649)</option>
            <option value="550">Very Poor (Below 600)</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="employmentStatus" className="bank-form-label">
          Employment Status <span className="text-danger">*</span>
        </label>
        <select
          className="form-select bank-form-control"
          id="employmentStatus"
          name="employmentStatus"
          value={formData.employmentStatus}
          onChange={handleInputChange}
        >
          <option value="employed">Employed Full-Time</option>
          <option value="part-time">Employed Part-Time</option>
          <option value="self-employed">Self-Employed</option>
          <option value="retired">Retired</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="d-flex justify-content-between">
        <button type="button" id="loan-step2-back-btn" className="btn btn-outline-secondary" onClick={() => goToStep(1)}>
          <i className="bi bi-arrow-left me-2"></i> Back
        </button>
        <button type="button" id="loan-step2-continue-btn" className="bank-btn-primary" onClick={handleNext}>
          Continue <i className="bi bi-arrow-right ms-2"></i>
        </button>
      </div>
    </div>
  );

  // Step 3: Review & Submit
  const renderStep3 = () => (
    <div>
      <h5 className="mb-4">
        <i className="bi bi-clipboard-check me-2"></i>
        Review Your Application
      </h5>

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-subtitle mb-3 text-muted">Vehicle Details</h6>
          <p className="mb-1">
            <strong>Vehicle:</strong> {getVehicleString()}
          </p>
          <p className="mb-1">
            <strong>Loan Amount:</strong> ${formData.amount}
          </p>
          <p className="mb-0">
            <strong>Term:</strong> {formData.termMonths} months
          </p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-subtitle mb-3 text-muted">Applicant Information</h6>
          <p className="mb-1">
            <strong>Name:</strong> {formData.name}
          </p>
          <p className="mb-1">
            <strong>SSN:</strong> ***-**-{formData.ssn.slice(-4)}
          </p>
          <p className="mb-1">
            <strong>Annual Income:</strong> ${formData.income}
          </p>
          <p className="mb-0">
            <strong>Credit Score:</strong> {formData.creditScore}+
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="agreeTerms"
            name="agreeTerms"
            checked={formData.agreeTerms}
            onChange={handleInputChange}
          />
          <label className="form-check-label" htmlFor="agreeTerms">
            I agree to the{' '}
            <a href="#" className="text-datadog-purple">
              Terms and Conditions
            </a>{' '}
            and authorize Bitsburg Bank to verify my credit information. <span className="text-danger">*</span>
          </label>
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <button type="button" id="loan-step3-back-btn" className="btn btn-outline-secondary" onClick={() => goToStep(2)}>
          <i className="bi bi-arrow-left me-2"></i> Back
        </button>
        <button type="submit" id="loan-submit-btn" className="bank-btn-primary" disabled={isLoading}>
          {!isLoading ? (
            <span>
              <i className="bi bi-send me-2"></i>Submit Application
            </span>
          ) : (
            <span>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Processing...
            </span>
          )}
        </button>
      </div>
    </div>
  );

  // Result display
  const renderResult = () => {
    if (!result) return null;

    const isApproved = result.status === 'Approved';
    const isPending = result.status === 'Pending Review';

    return (
      <div
        id="loan-result"
        className={`alert ${isApproved ? 'bank-alert bank-alert-success' : isPending ? 'alert-warning' : 'alert-danger'}`}
      >
        <h5 className="mb-3">
          <i
            className={`bi ${isApproved ? 'bi-check-circle' : isPending ? 'bi-hourglass-split' : 'bi-x-circle'} me-2`}
          ></i>
          {result.status}
        </h5>
        <p className="mb-2">{result.message}</p>
        {result.applicationId && (
          <p className="mb-2">
            <strong>Application ID:</strong> {result.applicationId}
          </p>
        )}
        {isApproved && result.interestRate && result.monthlyPayment && (
          <div className="mt-3 p-3 bg-white rounded">
            <p className="mb-1">
              <strong>Interest Rate:</strong> {result.interestRate}% APR
            </p>
            <p className="mb-0">
              <strong>Estimated Monthly Payment:</strong> ${result.monthlyPayment?.toFixed(2)}
            </p>
          </div>
        )}
        <div className="mt-4">
          <Link to="/" id="loan-result-home-btn" className="btn btn-outline-primary">
            <i className="bi bi-house me-2"></i>Return to Home
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 col-md-9">
            <div className="bank-card bank-card-elevated bank-card-no-hover">
              <div className="bank-card-header">
                <h3 className="mb-0 fw-bold">
                  <i className="bi bi-car-front me-2"></i>
                  Auto Loan Application
                </h3>
                <p className="mb-0 mt-2 opacity-75">Get pre-approved in minutes</p>
              </div>
              <div className="bank-card-body">
                {result ? (
                  renderResult()
                ) : (
                  <>
                    {renderProgress()}

                    {error && (
                      <div className="alert bank-alert bank-alert-danger mb-4">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {currentStep === 1 && renderStep1()}
                      {currentStep === 2 && renderStep2()}
                      {currentStep === 3 && renderStep3()}
                    </form>
                  </>
                )}

                <div className="text-center mt-4">
                  <Link to="/" id="loan-back-home-link" className="text-datadog-purple text-decoration-none" onClick={handleAbandon}>
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanPage;
